import React from "react";
import { Box, Typography, CardContent, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { formatCurrency } from "../../../utils/currencyUtils";
import { CategoryPerformanceBiDto } from "../../../shared/dtos/bi/CategoryPerformanceBiDto";

interface CategoryPerformanceWidgetProps {
  data?: CategoryPerformanceBiDto[];
}

const CategoryPerformanceWidget: React.FC<CategoryPerformanceWidgetProps> = ({
  data = [],
}) => {
  const theme = useTheme();
  const colorRev = theme.palette.secondary.main;
  const colorProf = "#ff9800";

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
          Category Performance
        </Typography>

        {data.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke={theme.palette.neutral.dark}
                />
                <XAxis
                  dataKey="categoryName"
                  tick={{ fontSize: 11, fill: theme.palette.neutral.light }}
                  tickMargin={10}
                  tickFormatter={(val) =>
                    val.length > 15 ? `${val.substring(0, 15)}...` : val
                  }
                />
                <YAxis
                  tickFormatter={(val) =>
                    new Intl.NumberFormat("en-US", {
                      notation: "compact",
                    }).format(val)
                  }
                  tick={{ fontSize: 11, fill: theme.palette.neutral.light }}
                  tickMargin={10}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 8,
                    borderColor: theme.palette.neutral.dark,
                    color: theme.palette.neutral.light,
                  }}
                  formatter={(val) => formatCurrency(Number(val))}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: "10px",
                    color: theme.palette.neutral.light,
                  }}
                />
                <Bar
                  dataKey="totalRevenue"
                  name="Revenue"
                  fill={colorRev}
                  radius={[4, 4, 0, 0]}
                  barSize={25}
                />
                <Bar
                  dataKey="totalProfit"
                  name="Profit"
                  fill={colorProf}
                  radius={[4, 4, 0, 0]}
                  barSize={25}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 300,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No category data found.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default CategoryPerformanceWidget;
